// biome-ignore-all lint/suspicious/noExplicitAny: internal type-erased dispatch — the configure phase coordinates opaque module configs without knowing their concrete shapes
/* eslint-disable @typescript-eslint/no-explicit-any */
import { from, lastValueFrom } from 'rxjs';
import { mergeMap, reduce } from 'rxjs/operators';
import { ModuleEventLevel } from '../../../types.js';
import { ModuleConfiguratorEventName } from '../module-configurator-event-name.js';
/**
 * Creates the raw module config map by calling each module's `configure` factory.
 *
 * Runs all module `configure(ref)` calls concurrently via `mergeMap` and
 * accumulates results into a single config object. The object is seeded with
 * `onAfterConfiguration` and `onAfterInit` helpers that allow modules to
 * register additional post-phase callbacks during configuration.
 *
 * @param ctx - The configure phase context.
 * @param ref - Optional reference forwarded to each module's configure factory.
 * @returns A promise resolving to the merged module config map.
 * @template TRef - Reference type forwarded to module configure hooks.
 * @throws Re-throws any error from a module's `configure` factory after emitting a failure event.
 * @internal
 */
export async function createModuleConfigs(ctx, ref) {
    const { modules, afterConfiguration, afterInit, registerEvent } = ctx;
    // Run each module's configure factory concurrently and track timing/failures
    const config$ = from(modules).pipe(mergeMap(async (module) => {
        const configStart = performance.now();
        try {
            const configurator = await module.configure?.(ref);
            const configLoadTime = Math.round(performance.now() - configStart);
            registerEvent({
                level: ModuleEventLevel.Debug,
                name: ModuleConfiguratorEventName.ConfiguratorCreated,
                message: `Configurator created for ${module.name} in ${configLoadTime}ms`,
                properties: {
                    moduleName: module.name,
                    moduleVersion: module.version?.toString() || 'unknown',
                    configLoadTime,
                },
                metric: configLoadTime,
            });
            return { [module.name]: configurator };
        }
        catch (err) {
            registerEvent({
                level: ModuleEventLevel.Error,
                name: ModuleConfiguratorEventName.ConfiguratorFailed,
                message: `Failed to create configurator for ${module.name}`,
                properties: {
                    moduleName: module.name,
                    moduleVersion: module.version?.toString() || 'unknown',
                },
                metric: Math.round(performance.now() - configStart),
                error: err,
            });
            throw err;
        }
    }), reduce((acc, module) => {
        // Merge each module's config object into the shared accumulator
        const merged = Object.assign(acc, module);
        return merged;
    }, 
    // Seed the config object with hooks so modules can register post-phase callbacks
    // during their own configure factory (a common pattern for cross-module wiring).
    {
        onAfterConfiguration(cb) {
            afterConfiguration.push(cb);
        },
        onAfterInit(cb) {
            afterInit.push(cb);
        },
    }));
    return lastValueFrom(config$);
}
//# sourceMappingURL=create-module-configs.js.map